import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCombinedRefs from '../hooks/use-combined-ref';
import { getFocusedContainer, setFocusedContainer } from '../store/uiSlice';

const FixedUIContainer = forwardRef<
    HTMLDivElement,
    { children: React.ReactNode }
>((props, ref) => {
    const dispatch = useDispatch();
    const focusedContainer = useSelector(getFocusedContainer);
    const containerRef = useRef<HTMLDivElement>(null);
    const combinedRef = useCombinedRefs(ref, containerRef);

    // Utility to only dispatch when value changes
    const setFocusedContainerIfDifferent = useCallback((container: 'fixed' | 'scrollable' | null) => {
        if (focusedContainer !== container) {
            dispatch(setFocusedContainer(container));
        }
    }, [dispatch, focusedContainer]);

    useEffect(() => {
        const handleFocus = () => {
            setFocusedContainerIfDifferent('fixed');
        };

        const handleBlur = (e: FocusEvent) => {
            if (!containerRef.current?.contains(e.relatedTarget as Node)) {
                const scrollableContainer = document.getElementById('atarim-collab-scrollable-container');
                if (!scrollableContainer?.contains(e.relatedTarget as Node)) {
                    setFocusedContainerIfDifferent('fixed');
                }
            }
        };

        // Add pointer enter/move event handlers for hover detection
        const handlePointerEnter = () => {
            setFocusedContainerIfDifferent('fixed');
        };

        const handlePointerMove = () => {
            setFocusedContainerIfDifferent('fixed');
        };

        const currentRef = containerRef.current;
        if (currentRef) {
            // Existing event listeners
            currentRef.addEventListener('focusin', handleFocus, true);
            currentRef.addEventListener('blur', handleBlur, true);

            // Add hover event listeners
            currentRef.addEventListener('pointerenter', handlePointerEnter);
            currentRef.addEventListener('pointermove', handlePointerMove);

            // Handle child elements
            const allChildren = currentRef.querySelectorAll('*');
            allChildren.forEach(el => {
                el.addEventListener('pointerenter', handlePointerEnter);
                el.addEventListener('focus', handleFocus);
            });
        }

        return () => {
            if (currentRef) {
                // Remove existing listeners
                currentRef.removeEventListener('focusin', handleFocus, true);
                currentRef.removeEventListener('blur', handleBlur, true);

                // Remove hover listeners
                currentRef.removeEventListener('pointerenter', handlePointerEnter);
                currentRef.removeEventListener('pointermove', handlePointerMove);

                // Clean up child element listeners
                const allChildren = currentRef.querySelectorAll('*');
                allChildren.forEach(el => {
                    el.removeEventListener('pointerenter', handlePointerEnter);
                    el.removeEventListener('focus', handleFocus);
                });
            }
        };
    }, [setFocusedContainerIfDifferent]);

    // Click and mouse hover handlers
    const handleClick = () => {
        setFocusedContainerIfDifferent('fixed');
    };

    const handleMouseOver = () => {
        setFocusedContainerIfDifferent('fixed');
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            const activeElement = document.activeElement;
            const scrollableContainer = document.getElementById('atarim-collab-scrollable-container');

            if (!scrollableContainer?.contains(activeElement) &&
                !scrollableContainer?.contains(document.elementFromPoint(
                    window.innerWidth / 2,
                    window.innerHeight / 2
                ) as Node)) {
                setFocusedContainerIfDifferent('fixed');
            }
        }, 50);
    };

    const handleMouseEnter = () => {
        setFocusedContainerIfDifferent('fixed');
    };

    const zIndex = focusedContainer === 'fixed' ? 30 : 20;

    return (
        <div
            ref={combinedRef}
            id="atarim-collab-fixed-ui-container"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none', // Don't change this. It's important for the overlay to work
                isolation: 'isolate',
                zIndex
            }}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {props.children}
        </div>
    );
});

FixedUIContainer.displayName = 'FixedUIContainer';

export default FixedUIContainer;